import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ParticleBackground } from '../components/ParticleBackground';
import { Navbar } from '../components/Navbar/Navbar';

const TermsOfService: React.FC = () => {
  const [animationPhase, setAnimationPhase] = useState(0);
  
  // Force scroll to top on page load
  useEffect(() => {
    // Set document title
    document.title = 'Terms of Service - MockTrail';
    
    window.scrollTo(0, 0);
    
    // Start animation
    setTimeout(() => setAnimationPhase(1), 1600);
  }, []);

  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    overview: true,
    accounts: true,
    usage: true,
    intellectual: true,
    ai: true,
    fees: true,
    termination: true,
    disclaimer: true,
    liability: true,
    indemnification: true,
    governing: true,
    changes: true,
    contact: true,
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Reusable section component
  const TermsSection = ({ 
    id, 
    title, 
    children 
  }: { 
    id: string, 
    title: string, 
    children: React.ReactNode 
  }) => (
    <div className="mb-8 border-b border-border-light dark:border-border-dark pb-6">
      <button 
        className="flex w-full justify-between items-center mb-4 text-left"
        onClick={() => toggleSection(id)}
      >
        <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          {title}
        </h2>
        {expandedSections[id] ? 
          <ChevronUp className="text-text-secondary-light dark:text-text-secondary-dark" /> : 
          <ChevronDown className="text-text-secondary-light dark:text-text-secondary-dark" />
        }
      </button>
      {expandedSections[id] && (
        <div className="text-base text-text-secondary-light dark:text-text-secondary-dark space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-7xl mt-32">
        <div className={`mb-12 text-center transform transition-all duration-500 ease-out ${
          animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="flex justify-center mb-4">
          </div>
          <h1 className="text-4xl font-heading font-bold mb-4">Terms of Service</h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
            Last updated: May 21, 2025
          </p>
        </div>
        
        <div className={`w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 transform transition-all duration-500 ease-out ${
          animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`} style={{ transitionDelay: '200ms' }}>
          <TermsSection id="overview" title="Overview">
            <p>
              Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the Mocktrail platform (the "Service") operated by Aurivance Technologies LLP ("us", "we", or "our").
            </p>
            <p>
              Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who wish to access or use the Service.
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you do not have permission to access the Service.
            </p>
          </TermsSection>
          
          <TermsSection id="accounts" title="Accounts">
            <p>
              When you create an account with us, you guarantee that you are above the age of 13 and that the information you provide us is accurate, complete, and current at all times. Inaccurate, incomplete, or obsolete information may result in the immediate termination of your account on the Service.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account and password, including but not limited to the restriction of access to your computer and/or account. You agree to accept responsibility for any and all activities or actions that occur under your account and/or password. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
            <p>
              You may not use as a username the name of another person or entity or that is not lawfully available for use, a name or trademark that is subject to any rights of another person or entity other than you, without appropriate authorization. You may not use as a username any name that is offensive, vulgar, or obscene.
            </p>
          </TermsSection>
          
          <TermsSection id="usage" title="Use of the Service">
            <p>
              Mocktrail provides an AI-based platform for interview practice. You agree to use the Service for its intended purpose and in compliance with all applicable laws and regulations.
            </p>
            <p>
              You are solely responsible for the content you create, share, or upload during your use of the Service, including video and audio recordings of your mock interviews ("User Content").
            </p>
            <p>
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Upload, post, email, transmit, or otherwise make available any content that is unlawful, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially, ethnically, or otherwise objectionable.</li>
              <li>Harm minors in any way.</li>
              <li>Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity.</li>
              <li>Forge headers or otherwise manipulate identifiers in order to disguise the origin of any content transmitted through the Service.</li>
              <li>Upload, post, email, transmit, or otherwise make available any content that you do not have a right to make available under any law or under contractual or fiduciary relationships.</li>
              <li>Upload, post, email, transmit, or otherwise make available any content that infringes any patent, trademark, trade secret, copyright, or other proprietary rights of any party.</li>
              <li>Upload, post, email, transmit, or otherwise make available any unsolicited or unauthorized advertising, promotional materials, "junk mail," "spam," "chain letters," "pyramid schemes," or any other form of solicitation.</li>
              <li>Upload, post, email, transmit, or otherwise make available any material that contains software viruses or any other computer code, files, or programs designed to interrupt, destroy, or limit the functionality of any computer software or hardware or telecommunications equipment.</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service, or disobey any requirements, procedures, policies, or regulations of networks connected to the Service.</li>
            </ul>
          </TermsSection>
          
          <TermsSection id="intellectual" title="Intellectual Property">
            <p>
              The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of Aurivance Technologies LLP and its licensors. The Service is protected by copyright, trademark, and other laws of both India and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Aurivance Technologies LLP.
            </p>
            <p>
              By submitting User Content on or through the Service, you grant us a worldwide, non-exclusive, royalty-free license (with the right to sublicense) to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such User Content in any and all media or distribution methods (now known or later developed) for the purposes of operating, developing, providing, and improving the Service and researching and developing new ones.
            </p>
            <p>
              You represent and warrant that you have all the rights, power, and authority necessary to grant the rights granted herein to any User Content that you submit.
            </p>
          </TermsSection>
          
          <TermsSection id="ai" title="AI-Generated Feedback">
            <p>
              The Service utilizes artificial intelligence (AI) to provide feedback on your mock interview performance. This feedback is generated based on algorithms and data analysis.
            </p>
            <p>
              While we strive to provide accurate and helpful feedback, you acknowledge that AI-generated feedback may not always be perfect or complete and should be considered as a tool for improvement, not as a definitive assessment of your abilities or a guarantee of future interview success.
            </p>
            <p>
              Aurivance Technologies LLP is not liable for any decisions you make based on the AI-generated feedback provided by the Service.
            </p>
          </TermsSection>
          
          <TermsSection id="fees" title="Fees and Payment">
            <p>
              Some parts of the Service may be subject to fees. You will be notified of any applicable fees before you incur them.
            </p>
            <p>
              All fees are quoted in Indian Rupees (INR).
            </p>
            <p>
              You agree to pay all fees and applicable taxes incurred by you or anyone using an account registered to you.
            </p>
            <p>
              We reserve the right to change our pricing and payment terms at any time. Changes to the pricing or payment terms will be posted on the Service and will be effective immediately.
            </p>
          </TermsSection>
          
          <TermsSection id="termination" title="Termination">
            <p>
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
            </p>
            <p>
              If you wish to terminate your account, you may simply discontinue using the Service or contact us to request account deletion.
            </p>
            <p>
              All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </TermsSection>
          
          <TermsSection id="disclaimer" title="Disclaimer of Warranties">
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.
            </p>
            <p>
              AURIVANCE TECHNOLOGIES LLP EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              AURIVANCE TECHNOLOGIES LLP MAKES NO WARRANTY THAT (I) THE SERVICE WILL MEET YOUR REQUIREMENTS, (II) THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, (III) THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE SERVICE (INCLUDING AI-GENERATED FEEDBACK) WILL BE ACCURATE OR RELIABLE, (IV) THE QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER MATERIAL PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICE WILL MEET YOUR EXPECTATIONS, AND (V) ANY ERRORS IN THE SOFTWARE WILL BE CORRECTED.
            </p>
          </TermsSection>
          
          <TermsSection id="liability" title="Limitation of Liability">
            <p>
              IN NO EVENT SHALL AURIVANCE TECHNOLOGIES LLP, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (III) ANY CONTENT OBTAINED FROM THE SERVICE (INCLUDING AI-GENERATED FEEDBACK); AND (IV) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
            </p>
          </TermsSection>
          
          <TermsSection id="indemnification" title="Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless Aurivance Technologies LLP and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or any person using your account and password; b) a breach of these Terms, or c) User Content posted on the Service.
            </p>
          </TermsSection>
          
          <TermsSection id="governing" title="Governing Law">
            <p>
              These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service and supersede and replace any prior agreements we might have had between us regarding the Service.
            </p>
          </TermsSection>
          
          <TermsSection id="changes" title="Changes to Terms of Service">
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p>
              By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
            </p>
          </TermsSection>
          
          <TermsSection id="contact" title="Contact Us">
            <p>
              If you have questions or comments about these Terms, please contact us:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Aurivance Technologies LLP</li>
              <li>Email: support@mocktrail.app, office@aurivancetech.com</li>
              <li>Phone: +91 7004297500, +91 9877253751</li>
              <li>Address: 1035 1st floor, Global City Sector 124, Kharar(Rupnagar), Punjab, India, 140301</li>
              <li>Website: https://mocktrail.app</li>
            </ul>
          </TermsSection>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 